<template>
    <div class="w-100 h-100 position-relative">
        <template v-for="(item,index) of chartGroupList">
            <table-chart v-if="rows.length>0 && forceReload" :key="index" :field-list="item.list"
                         ref="tableCharts"
                         :class="chartGroupList.length > 1 ? 'chart-half-height' : ''"
                         :x-axis-items="xAxisItems"
                         :group-id="groupId"></table-chart>
        </template>
        <t-c-logo v-if="chartCount===0" class="position-absolute top"></t-c-logo>
    </div>
</template>

<script>
import TableChart from "@/components/echarts-part/TableChart/cell/TableChart.vue";
import TableChartMixins, {AGGREGATION} from "@/components/echarts-part/TableChart/mixins/TableChartMixins";
import ExpressionMixins from "@/components/echarts-part/TableChart/mixins/ExpressionMixins";
import TCLogo from "@/components/echarts-part/TableChart/cell/TCLogo.vue";
import emCommonUtilMixin from "@/mixin/em/common/stuffs/utils/emCommonUtilMixin";
import * as echarts from "echarts";
import _ from "lodash";

export default {
    name: "TableChartViewer",
    components: {TCLogo, TableChart},
    mixins: [TableChartMixins, ExpressionMixins, emCommonUtilMixin],
    props: {
        /**
         * {
         *     list: [{
         *         key,
         *         item: {
         *             key, label, expression, isCustomized, isCountSum
         *         },
         *         type,
         *         isSeq,
         *         aggregation,//SUM|AVG|MIN|MAX
         *         groupBy: {
         *             fields: [{
         *                 key
         *             }],
         *             dateSplitUnit,
         *             stacked,
         *         },
         *         sortDesc,
         *     }],
         *     xAxisFields: [{
         *         key,
         *         form: {
         *             type
         *         }
         *     }],
         *     dateSplitUnit,
         * }
         */
        options: Object,
        rows: Array
    },
    data() {
        return {
            forceReload: true,
            groupId: null,
            chartGroupList: [],
            sortedXAxis: [],
        }
    },
    computed: {
        localeOptions() {
            return this.options ? this.options : {};
        },
        $_xAxisFields() {
            return this.localeOptions.xAxisFields ? this.localeOptions.xAxisFields : [];
        },
        $_dateSplitUnit() {
            return this.localeOptions.dateSplitUnit !== undefined ? this.localeOptions.dateSplitUnit : -1;
        },
        //charts X axis collection, to draw
        xAxisItems() {
            return this.getXAxisItems(this.rows ? this.rows : []);
        },

        chartCount() {
            return this.chartGroupList.length;
        },
    },
    watch: {
        chartCount(val, oldVal) {
            if (val > 1 ^ oldVal > 1) {
                // this.forceReload = false;
                // this.$nextTick(() => this.forceReload = true);
                this.resize();
            }
        },
        chartGroupList: {
            handler() {
                this.$nextTick(() => echarts.connect(this.groupId));
            },
            deep: true,
            immediate: true
        },
        options: {
            handler() {
                this.groupId = this.genNonDuplicateID(4);
                this.chartGroupList = this.getChartGroupList();
            },
            deep: true,
            immediate: true
        },
    },
    methods: {
        //charts data collection, to draw
        getChartGroupList() {
            this.sortedXAxis = [];
            let groupList = [];
            let list = this.localeOptions.list;
            if (list) {
                let tempList = [];
                for (let i = 0; i < list.length; i++) {
                    let item = list[i];
                    if (!item.isSeg) {
                        let groupValues = this.getGroupValues(item);
                        if (groupValues instanceof Array) {
                            let temp = {
                                type: item.type,
                                id: item.key,
                                name: this.fieldLabel(item.item),
                                data: groupValues
                            };
                            //gather sorted xAxis
                            if (item.sortDesc !== undefined) {
                                let orderBy = _.orderBy(groupValues, ['forSort'], [item.sortDesc ? 'desc' : 'asc']);
                                let tempNameList = [], tempValueList = [];
                                orderBy.forEach(o => {
                                    tempValueList.push(o.value);
                                    tempNameList.push(o.name);
                                });
                                temp.data = tempValueList;
                                this.sortedXAxis = tempNameList;
                            }
                            tempList.push(temp);
                        } else {
                            for (let k of Object.keys(groupValues)) {
                                let values = groupValues[k];
                                let series = {
                                    type: item.type,
                                    id: k + item.key,
                                    name: k + '-' + this.fieldLabel(item.item),
                                    data: values,
                                };
                                if (item.groupBy && item.groupBy.fields && item.groupBy.stacked) {
                                    series.stack = item.key + 'Stack';
                                    series.emphasis = {
                                        focus: 'series'
                                    }
                                }
                                tempList.push(series);
                            }
                        }
                    }
                    if (item.isSeg || i === list.length - 1) {
                        if (tempList.length > 0) {
                            groupList.push({
                                list: tempList
                            });
                            tempList = [];
                        }
                    }
                }
            }
            return groupList;
        },
        getXAxisItems(rows) {
            if (this.isXAxisTypeDate) {
                if (this.isXAxisTimeSplit) {
                    let dateArray = this.getSplitDateArray(rows,
                        row => this.getAxisItemName(row), this.$_dateSplitUnit);
                    if (dateArray != null) {
                        return dateArray;
                    }
                }
                return 'time';
            } else {
                if (this.sortedXAxis.length > 0) {
                    return this.sortedXAxis;
                }
                let tempSet = new Set();
                for (let row of rows) {
                    tempSet.add(this.getAxisItemName(row));
                }
                return Array.from(tempSet);
            }
        },
        //Cal series data for Y field
        getGroupValues(option) {
            let item = option.item, vars = null;
            if (item.isCustomized && item.expression) {
                vars = this.findVariablesOfExpression(item.expression);
            }
            let result;
            let isPie = option.type === 'pie';
            if (this.isFieldGrouped(option)) {
                result = {};
                this.groupRowsThen(this.rows, (grs, k) => {
                    result[k] = this.doGroupValues(grs, option, vars, isPie, option.groupBy.fields);
                }, option.groupBy);
            } else {
                result = this.doGroupValues(this.rows, option, vars, isPie, this.$_xAxisFields);
            }
            return result;
        },
        doGroupValues(rows, option, vars, isPie, fields) {
            let result = [];
            this.groupRowsThen(rows, (grs, name) => {
                let value = this.calValueOfRows(option, vars, grs);
                result.push({
                    name: name,
                    value: isPie ? value : [name, value],
                    forSort: Number.parseFloat(value)
                });
            });
            if (this.getXAxisType(fields)) {
                result.sort((a, b) => b.name > a.name ? -1 : 1);
            }
            return result;
        },
        groupRowsThen(rows, func, groupBy) {
            let groupRows = this.getGroupRows(rows, groupBy);
            for (let axisName of Object.keys(groupRows)) {
                let rows = groupRows[axisName];
                func(rows, axisName);
            }
        },
        calValueOfRows(option, expressionVars, rows) {
            let tempList = [];
            for (let row of rows) {
                let value = this.calValueOfRow(option.item, expressionVars, row);
                if (value != null) {
                    tempList.push(value);
                }
            }
            let value = this.getAggregateResult(tempList, option.aggregation);
            //保留小数点后三位
            value = value.toFixed(3);
            return value;
        },
        calValueOfRow(optionItem, expressionVars, row) {
            if (optionItem.isCountSum) {
                return 1;//计数
            } else {
                if (expressionVars != null) {
                    return this.calExpressionValue(optionItem.expression, expressionVars, row);
                } else {
                    return this.getNumberValue(row[optionItem.key]);
                }
            }
        },
        getGroupRows(rows, groupBy = {}) {
            let tempResult = {};
            for (let row of rows) {
                let axisName = this.getFormattedAxisItemName(row, groupBy.fields, groupBy.dateSplitUnit);
                if (!tempResult.hasOwnProperty(axisName)) {
                    tempResult[axisName] = [];
                }
                tempResult[axisName].push(row);
            }
            return tempResult;
        },
        getFormattedAxisItemName(row, axisFields = this.$_xAxisFields, dateSplitUnit = this.$_dateSplitUnit) {
            let axisName = this.getAxisItemName(row, axisFields);
            if (this.getXAxisType(axisFields) && this.getIsTimeSplit(dateSplitUnit)) {
                axisName = this.dateFormat(axisName, dateSplitUnit, true);
            }
            return axisName;
        },
        getAxisItemName(row, xAxisFields = this.$_xAxisFields) {
            let tempList = [];
            for (let item of xAxisFields) {
                let temp = row[item.key];
                tempList.push(temp ? temp : "?");
            }
            if (tempList.length === 0) {
                return undefined;
            }
            return tempList.join('_');
        },
        getAggregateResult(valueList, aggregation = AGGREGATION.SUM) {
            if (!valueList || valueList.length === 0) {
                return 0;
            }
            let result = 0, init = false;
            for (let v of valueList) {
                switch (aggregation) {
                    case AGGREGATION.SUM:
                    case AGGREGATION.AVG:
                        result += v;
                        break;
                    case AGGREGATION.MIN:
                        if (v < result || !init) {
                            result = v;
                            init = true;
                        }
                        break;
                    case AGGREGATION.MAX:
                        if (v > result || !init) {
                            result = v;
                            init = true;
                        }
                        break;
                }
            }
            if (aggregation === AGGREGATION.AVG) {
                result /= valueList.length;
            }
            return result;
        },

        resize() {
            this.$nextTick(() => {
                let tableCharts = this.$refs.tableCharts;
                if (tableCharts && tableCharts instanceof Array) {
                    for (let chart of tableCharts) {
                        chart.resize();
                    }
                }
            });
        },
    }

}
</script>

<style scoped>
.chart-half-height {
    height: 50% !important;
}
</style>
